import React, { useState, useCallback, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => {
  try {
    return localStorage.getItem('token') || localStorage.getItem('access_token') || '';
  } catch { return ''; }
};

const authFetch = async (path, options = {}) => {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${path}`, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.response = { data, status: res.status };
    throw err;
  }
  return data;
};

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function LiveTestPanel({ language = 'hi' }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [form, setForm] = useState({
    amount: 10,
    school_name: 'Production Test School',
    contact_name: 'Vikas Garg',
    contact_phone: '9826618603',
    contact_email: 'info.sostcs@gmail.com',
  });

  const t = (hi, en) => (language === 'hi' ? hi : en);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const loadOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const data = await authFetch('/api/razorpay/admin/test-orders?limit=15');
      setOrders(data?.orders || []);
    } catch (e) {
      // silent — section just shows empty
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadOrders();
  }, [open, loadOrders]);

  const handleStart = async () => {
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error('Razorpay SDK load failed');

      const orderData = await authFetch('/api/razorpay/admin/create-test-order', {
        method: 'POST',
        body: JSON.stringify({
          amount: parseInt(form.amount, 10),
          school_name: form.school_name,
          contact_name: form.contact_name,
          contact_phone: form.contact_phone,
          contact_email: form.contact_email,
        }),
      });
      if (!orderData?.success) throw new Error(orderData?.detail || 'Order creation failed');

      if (orderData.is_mock) {
        const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            razorpay_order_id: orderData.order.id,
            razorpay_payment_id: `pay_mock_${Date.now()}`,
            razorpay_signature: 'mock_signature',
            school_id: orderData.test_school_id,
          }),
        });
        const verifyData = await verifyRes.json();
        setResult({ mock: true, ...verifyData });
        setSubmitting(false);
        return;
      }

      const options = {
        key: orderData.key_id,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'शिक्षा मित्र — Live Test',
        description: t(`उत्पादन ₹${form.amount} live test`, `Production ₹${form.amount} live test`),
        order_id: orderData.order.id,
        prefill: {
          name: form.contact_name,
          email: form.contact_email,
          contact: form.contact_phone,
        },
        theme: { color: '#f97316' },
        handler: async (resp) => {
          try {
            const verifyRes = await fetch(`${API_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                school_id: orderData.test_school_id,
              }),
            });
            const verifyData = await verifyRes.json();
            setResult({ mock: false, ...verifyData, paymentId: resp.razorpay_payment_id });
            loadOrders();
          } catch (verr) {
            setError('Verify failed: ' + (verr?.message || verr));
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            setError(t('आपने checkout बंद कर दिया।', 'Checkout dismissed.'));
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (err) => {
        setError(err?.error?.description || 'Payment failed');
        setSubmitting(false);
      });
      rzp.open();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed');
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <div className="bg-white border border-orange-200 rounded-2xl p-4 flex items-center justify-between shadow-sm">
        <div>
          <h4 className="font-bold text-slate-900">
            🧪 {t('Live Production Payment Test', 'Live Production Payment Test')}
          </h4>
          <p className="text-sm text-slate-600 mt-1">
            {t(
              'Real ₹10 Razorpay order create करें और पूरा Paid-to-Live flow verify करें (auto-school provisioning + WA/Email credentials)।',
              'Create a real ₹10 Razorpay order and verify the full Paid-to-Live flow (school auto-provisioning + WA/Email credentials).'
            )}
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold whitespace-nowrap"
          data-testid="live-test-open-btn"
        >
          🧪 {t('Test खोलें', 'Open Test')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-orange-300 rounded-2xl p-5 shadow-md" data-testid="live-test-panel">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-slate-900">🧪 {t('Live Razorpay Production Test', 'Live Razorpay Production Test')}</h4>
        <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-800 text-xl leading-none" aria-label="Close">
          ×
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        <Field label={t('Amount (₹1 - ₹500)', 'Amount (₹1 - ₹500)')}>
          <input
            type="number"
            name="amount"
            min={1}
            max={500}
            value={form.amount}
            onChange={handleChange}
            data-testid="live-test-amount"
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
        </Field>
        <Field label={t('School Name', 'School Name')}>
          <input name="school_name" value={form.school_name} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </Field>
        <Field label={t('Contact Name', 'Contact Name')}>
          <input name="contact_name" value={form.contact_name} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </Field>
        <Field label={t('WhatsApp Phone', 'WhatsApp Phone')}>
          <input name="contact_phone" value={form.contact_phone} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </Field>
        <Field label={t('Email', 'Email')} className="md:col-span-2">
          <input name="contact_email" value={form.contact_email} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg text-sm" />
        </Field>
      </div>

      <p className="text-xs text-slate-500 mb-3">
        {t(
          '⚠️ Real money charge होगा। Order success पर एक test school auto-provision होगा और credentials WhatsApp + Email पर भेजे जाएंगे।',
          '⚠️ Real money will be charged. On success, a test school will be auto-provisioned and credentials sent over WhatsApp + Email.'
        )}
      </p>

      <div className="flex gap-2">
        <button
          onClick={handleStart}
          disabled={submitting}
          data-testid="live-test-start-btn"
          className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg font-semibold"
        >
          {submitting ? t('चल रहा है...', 'Running...') : `🚀 ${t('Test Payment शुरू करें', 'Start Test Payment')}`}
        </button>
      </div>

      {error && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="live-test-error">
          ❌ {error}
        </div>
      )}

      {result && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm" data-testid="live-test-result">
          <div className="font-semibold text-emerald-800 mb-1">
            ✅ {t('Payment verified', 'Payment verified')} {result.mock ? '(MOCK)' : '(LIVE)'}
          </div>
          {result.auto_provision && (
            <div className="mt-2 text-emerald-900 space-y-1">
              <div><strong>School:</strong> {result.auto_provision.school_name} ({result.auto_provision.subdomain})</div>
              <div><strong>Login URL:</strong> {result.auto_provision.login_url}</div>
              <div><strong>Phone:</strong> {result.auto_provision.contact_phone}</div>
              {result.auto_provision.temporary_password && (
                <div><strong>Temp Password:</strong> <code className="bg-white px-1 rounded">{result.auto_provision.temporary_password}</code></div>
              )}
              <div className="text-xs text-emerald-700 mt-1">
                {t('Credentials WhatsApp + Email पर भेज दिए गए हैं।', 'Credentials dispatched over WhatsApp + Email.')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recent Test Orders Tracking */}
      <div className="mt-5 border-t pt-4" data-testid="live-test-orders-section">
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-semibold text-slate-800 text-sm">
            📋 {t('हाल के Live Test Orders', 'Recent Live Test Orders')}
          </h5>
          <button
            onClick={loadOrders}
            disabled={loadingOrders}
            className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded disabled:opacity-50"
            data-testid="live-test-orders-refresh"
          >
            {loadingOrders ? '⏳' : '↻'} {t('Refresh', 'Refresh')}
          </button>
        </div>
        {orders.length === 0 ? (
          <p className="text-xs text-slate-500 py-3 text-center">
            {loadingOrders ? t('Loading...', 'Loading...') : t('अभी तक कोई test order नहीं', 'No test orders yet')}
          </p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {orders.map((o) => (
              <div key={o.order_id} className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid={`live-test-order-${o.order_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <code className="font-mono text-slate-700">{o.order_id}</code>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${
                      o.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                      o.status === 'created' ? 'bg-amber-100 text-amber-700' :
                      'bg-rose-100 text-rose-700'
                    }`}>{o.status}</span>
                    <span className="font-bold text-slate-900">₹{o.amount_inr}</span>
                    {o.is_mock && <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[10px]">MOCK</span>}
                  </div>
                </div>
                <div className="mt-1 text-slate-600 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-0.5">
                  <div><strong className="text-slate-700">{t('स्कूल', 'School')}:</strong> {o.school_name || '—'}</div>
                  <div><strong className="text-slate-700">{t('संपर्क', 'Contact')}:</strong> {o.contact_name} / {o.contact_phone}</div>
                  {o.payment_id && <div><strong className="text-slate-700">Payment ID:</strong> <code>{o.payment_id}</code></div>}
                  <div><strong className="text-slate-700">{t('बनाया', 'Created')}:</strong> {o.created_at?.slice(0, 19).replace('T', ' ')}</div>
                  {o.verified_at && <div><strong className="text-slate-700">{t('Verified', 'Verified')}:</strong> {o.verified_at.slice(0, 19).replace('T', ' ')}</div>}
                  {o.credentials_whatsapp_status && (
                    <div>
                      <strong className="text-slate-700">{t('Credentials WA', 'Credentials WA')}:</strong>{' '}
                      <span className={o.credentials_whatsapp_status === 'sent' ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
                        {o.credentials_whatsapp_status}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[11px] text-slate-500 mt-2">
          💡 {t('Razorpay Dashboard पर पूरा details देखने के लिए: ', 'For full details visit: ')}
          <a href="https://dashboard.razorpay.com/app/payments" target="_blank" rel="noreferrer" className="underline text-blue-600">
            dashboard.razorpay.com → Payments
          </a>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold text-slate-700 mb-1">{label}</span>
      {children}
    </label>
  );
}
